import { useState } from 'react';

const categories = [
  '小学', '初中', '高中', '高考冲刺', '考研', '英语/托福/雅思',
  '英语四六级', '会计考试', 'CPA注册会计师', '护士执照考试', '执业医师考试', '法律职业资格', '大学课程',
];

interface Props {
  onClose: () => void;
}

export default function UploadVideoModal({ onClose }: Props) {
  const [step, setStep] = useState<1 | 2>(1);
  const [form, setForm] = useState({
    title: '', category: '', grade: '', description: '', price: '',
    originalPrice: '', duration: '', videoFile: '', thumbnailFile: '',
    freePreview: true,
  });
  const [uploading, setUploading] = useState(false);
  const [uploaded, setUploaded] = useState(false);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setUploading(true);
    await new Promise(r => setTimeout(r, 2000));
    setUploading(false);
    setUploaded(true);
  };

  return (
    <div className="fixed inset-0 z-[200] flex items-center justify-center p-4 bg-black/70 backdrop-blur-sm">
      <div className="bg-white w-full max-w-2xl rounded-3xl overflow-hidden shadow-2xl max-h-[90vh] flex flex-col">
        {/* Header */}
        <div className="px-6 py-4 border-b border-gray-100 flex items-center justify-between flex-shrink-0">
          <div>
            <h2 className="font-black text-gray-900 text-lg">上传新课程</h2>
            <p className="text-xs text-gray-400 mt-0.5">第{step}步 / 共2步</p>
          </div>
          <button onClick={onClose} className="text-gray-400 hover:text-gray-700 cursor-pointer transition-colors">
            <i className="ri-close-line text-2xl"></i>
          </button>
        </div>

        {/* Progress */}
        <div className="px-6 pt-4 flex-shrink-0">
          <div className="flex gap-2">
            <div className={`flex-1 h-1.5 rounded-full transition-all ${step >= 1 ? 'bg-red-600' : 'bg-gray-200'}`}></div>
            <div className={`flex-1 h-1.5 rounded-full transition-all ${step >= 2 ? 'bg-red-600' : 'bg-gray-200'}`}></div>
          </div>
        </div>

        {uploaded ? (
          <div className="flex-1 flex flex-col items-center justify-center p-10 text-center">
            <div className="w-20 h-20 flex items-center justify-center bg-green-100 rounded-full mb-5">
              <i className="ri-checkbox-circle-fill text-5xl text-green-500"></i>
            </div>
            <h3 className="text-2xl font-black text-gray-900 mb-2">课程提交成功！</h3>
            <p className="text-gray-500 text-sm mb-2">您的课程正在审核中，通常需要1-2个工作日</p>
            <p className="text-gray-400 text-xs mb-8">审核通过后将自动上架，学生即可购买</p>
            <button onClick={onClose} className="px-8 py-3 bg-red-600 text-white rounded-full font-bold hover:bg-red-700 transition-colors cursor-pointer">
              返回工作台
            </button>
          </div>
        ) : (
          <div className="flex-1 overflow-y-auto">
            {step === 1 && (
              <div className="p-6 flex flex-col gap-4">
                <div>
                  <label className="text-xs font-bold text-gray-600 mb-1.5 block">课程标题 *</label>
                  <input
                    type="text"
                    value={form.title}
                    onChange={e => setForm({ ...form, title: e.target.value })}
                    placeholder="如：高考数学压轴题全解析"
                    className="w-full bg-gray-50 border border-gray-200 rounded-xl px-4 py-3 text-sm outline-none focus:border-red-400 transition-colors"
                  />
                </div>
                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <label className="text-xs font-bold text-gray-600 mb-1.5 block">课程分类 *</label>
                    <select
                      value={form.category}
                      onChange={e => setForm({ ...form, category: e.target.value })}
                      className="w-full bg-gray-50 border border-gray-200 rounded-xl px-4 py-3 text-sm outline-none focus:border-red-400 transition-colors cursor-pointer"
                    >
                      <option value="">请选择分类</option>
                      {categories.map(c => <option key={c} value={c}>{c}</option>)}
                    </select>
                  </div>
                  <div>
                    <label className="text-xs font-bold text-gray-600 mb-1.5 block">适合年级/人群 *</label>
                    <input
                      type="text"
                      value={form.grade}
                      onChange={e => setForm({ ...form, grade: e.target.value })}
                      placeholder="如：高三 / 大学生"
                      className="w-full bg-gray-50 border border-gray-200 rounded-xl px-4 py-3 text-sm outline-none focus:border-red-400 transition-colors"
                    />
                  </div>
                </div>
                <div>
                  <label className="text-xs font-bold text-gray-600 mb-1.5 block">课程简介 *</label>
                  <textarea
                    value={form.description}
                    onChange={e => { if (e.target.value.length <= 500) setForm({ ...form, description: e.target.value }); }}
                    placeholder="详细描述课程内容、学习目标、适合人群..."
                    className="w-full bg-gray-50 border border-gray-200 rounded-xl px-4 py-3 text-sm outline-none focus:border-red-400 transition-colors resize-none h-28"
                    maxLength={500}
                  ></textarea>
                  <div className="text-right text-xs text-gray-400 mt-1">{form.description.length}/500</div>
                </div>
                <div className="grid grid-cols-3 gap-4">
                  <div>
                    <label className="text-xs font-bold text-gray-600 mb-1.5 block">售价（元）*</label>
                    <div className="flex items-center bg-gray-50 border border-gray-200 rounded-xl px-3 py-3 gap-1 focus-within:border-red-400 transition-colors">
                      <span className="text-gray-400 text-sm">¥</span>
                      <input
                        type="number"
                        min="0"
                        step="0.1"
                        value={form.price}
                        onChange={e => setForm({ ...form, price: e.target.value })}
                        placeholder="29.9"
                        className="flex-1 bg-transparent text-sm outline-none"
                      />
                    </div>
                  </div>
                  <div>
                    <label className="text-xs font-bold text-gray-600 mb-1.5 block">原价（元）</label>
                    <div className="flex items-center bg-gray-50 border border-gray-200 rounded-xl px-3 py-3 gap-1 focus-within:border-red-400 transition-colors">
                      <span className="text-gray-400 text-sm">¥</span>
                      <input
                        type="number"
                        min="0"
                        step="0.1"
                        value={form.originalPrice}
                        onChange={e => setForm({ ...form, originalPrice: e.target.value })}
                        placeholder="59.9"
                        className="flex-1 bg-transparent text-sm outline-none"
                      />
                    </div>
                  </div>
                  <div>
                    <label className="text-xs font-bold text-gray-600 mb-1.5 block">视频时长</label>
                    <input
                      type="text"
                      value={form.duration}
                      onChange={e => setForm({ ...form, duration: e.target.value })}
                      placeholder="如：45分钟"
                      className="w-full bg-gray-50 border border-gray-200 rounded-xl px-4 py-3 text-sm outline-none focus:border-red-400 transition-colors"
                    />
                  </div>
                </div>
                <div className="flex items-center gap-3 bg-green-50 border border-green-200 rounded-xl px-4 py-3">
                  <input
                    type="checkbox"
                    id="freePreview"
                    checked={form.freePreview}
                    onChange={e => setForm({ ...form, freePreview: e.target.checked })}
                    className="w-4 h-4 accent-green-600 cursor-pointer"
                  />
                  <label htmlFor="freePreview" className="text-sm text-green-800 font-medium cursor-pointer">
                    开启免费试听（前3分钟免费，吸引更多学生购买）
                  </label>
                </div>
                <button
                  onClick={() => {
                    if (!form.title || !form.category || !form.grade || !form.price) return;
                    setStep(2);
                  }}
                  className="w-full py-3.5 bg-red-600 text-white rounded-full font-bold hover:bg-red-700 transition-colors cursor-pointer"
                >
                  下一步：上传文件
                </button>
              </div>
            )}

            {step === 2 && (
              <form onSubmit={handleSubmit} className="p-6 flex flex-col gap-5">
                <button type="button" onClick={() => setStep(1)} className="flex items-center gap-1 text-gray-400 text-sm cursor-pointer hover:text-gray-600 w-fit">
                  <i className="ri-arrow-left-line"></i> 返回上一步
                </button>

                {/* Video Upload */}
                <div>
                  <label className="text-xs font-bold text-gray-600 mb-2 block">上传视频文件 *</label>
                  <div className="border-2 border-dashed border-gray-200 rounded-2xl p-8 text-center hover:border-red-300 transition-colors cursor-pointer group">
                    <div className="w-14 h-14 flex items-center justify-center bg-red-50 rounded-2xl mx-auto mb-3 group-hover:bg-red-100 transition-colors">
                      <i className="ri-video-upload-line text-red-500 text-2xl"></i>
                    </div>
                    <p className="text-sm font-bold text-gray-700 mb-1">点击或拖拽上传视频</p>
                    <p className="text-xs text-gray-400">支持 MP4、MOV、AVI 格式，最大 2GB</p>
                    <input type="file" accept="video/*" className="hidden" />
                    <button type="button" className="mt-4 px-5 py-2 bg-red-50 text-red-600 rounded-full text-sm font-semibold hover:bg-red-100 transition-colors cursor-pointer">
                      选择视频文件
                    </button>
                  </div>
                </div>

                {/* Thumbnail Upload */}
                <div>
                  <label className="text-xs font-bold text-gray-600 mb-2 block">上传封面图 *</label>
                  <div className="border-2 border-dashed border-gray-200 rounded-2xl p-6 text-center hover:border-red-300 transition-colors cursor-pointer group">
                    <div className="w-12 h-12 flex items-center justify-center bg-amber-50 rounded-2xl mx-auto mb-3 group-hover:bg-amber-100 transition-colors">
                      <i className="ri-image-add-line text-amber-500 text-xl"></i>
                    </div>
                    <p className="text-sm font-bold text-gray-700 mb-1">上传课程封面图</p>
                    <p className="text-xs text-gray-400">建议尺寸 16:9，JPG/PNG，最大 5MB</p>
                    <input type="file" accept="image/*" className="hidden" />
                    <button type="button" className="mt-3 px-5 py-2 bg-amber-50 text-amber-600 rounded-full text-sm font-semibold hover:bg-amber-100 transition-colors cursor-pointer">
                      选择封面图
                    </button>
                  </div>
                </div>

                {/* Summary */}
                <div className="bg-gray-50 rounded-2xl p-4 text-sm">
                  <h4 className="font-bold text-gray-900 mb-3">课程信息确认</h4>
                  <div className="grid grid-cols-2 gap-2 text-xs">
                    <div><span className="text-gray-400">标题：</span><span className="text-gray-700 font-medium">{form.title || '—'}</span></div>
                    <div><span className="text-gray-400">分类：</span><span className="text-gray-700 font-medium">{form.category || '—'}</span></div>
                    <div><span className="text-gray-400">售价：</span><span className="text-red-600 font-bold">¥{form.price || '—'}</span></div>
                    <div><span className="text-gray-400">时长：</span><span className="text-gray-700 font-medium">{form.duration || '—'}</span></div>
                  </div>
                </div>

                <div className="bg-amber-50 border border-amber-200 rounded-xl p-4 text-xs text-amber-800">
                  <i className="ri-information-line mr-1.5"></i>
                  提交后将进入审核流程（1-2个工作日），审核通过后课程自动上架。平台收取20%服务费，其余80%归您所有。
                </div>

                <button
                  type="submit"
                  disabled={uploading}
                  className="w-full py-4 bg-red-600 text-white rounded-full font-black text-base hover:bg-red-700 transition-colors cursor-pointer disabled:opacity-60"
                >
                  {uploading ? (
                    <span className="flex items-center justify-center gap-2">
                      <i className="ri-loader-4-line animate-spin"></i>上传中，请稍候...
                    </span>
                  ) : '提交课程审核'}
                </button>
              </form>
            )}
          </div>
        )}
      </div>
    </div>
  );
}
